/**
 * Priorität eines Vorgangs auf Stufe, Beschriftung und Farbe bringen.
 *
 * **In derselben Jira-Instanz leben ZWEI Prioritätsschemata.** Gemessen am
 * 2026-09-09 an den Feldmetadaten des Serviceprojekts MS und an einem Vorgang
 * aus einem anderen Projekt:
 *
 * - **Das Serviceprojekt** führt vier Stufen, die schlicht `1` bis `4` heißen.
 *   Gemeint ist die SAP-Meldungspriorität: 1 sehr hoch, 2 hoch, 3 mittel,
 *   4 niedrig. **Vorgabewert des Projekts ist `4`** — wer eine andere Stufe
 *   sieht, sieht eine Hochstufung von Hand.
 * - **Andere Projekte** nutzen das klassische Jira-Schema mit Namen („Mittel",
 *   „High", …), dessen Vorgabe „Mittel" ist.
 *
 * **Ausgeschrieben wird, was ÜBER der Vorgabe des jeweiligen Schemas steht** —
 * nicht ab einer festen Stufe. Der Grund steckt im Unterschied selbst: Eine
 * Priorität auf Vorgabewert ist keine Aussage, sie ist das, was beim Anlegen
 * ohnehin eingetragen wird. Erst die Abweichung ist eine Entscheidung, und die
 * gehört ins Daily. Deshalb erscheint im Serviceprojekt auch „Mittel" (Stufe 3)
 * am Vorgang, im klassischen Schema aber nicht: Dort **ist** Mittel die Vorgabe.
 *
 * Die vorige Fassung rechnete beide Schemata auf eine gemeinsame fünfstufige
 * Skala um und schrieb erst ab „Hoch" etwas hin. Damit blieb im Serviceprojekt
 * jede Hochstufung auf 3 unsichtbar — ein hochgestufter Vorgang sah aus wie
 * jeder andere (Rückmeldung vom 2026-09-09 zu MS-13044).
 *
 * Der Balken links an der Ticketzeile trägt die Farbe **immer**, auch auf der
 * Vorgabestufe; nur der Text kommt hinzu. Ein Wert, den wir nicht deuten
 * können, landet auf „mittel" und **ohne** Hervorhebung: Ihn ungefragt
 * herauszustellen wäre so falsch wie ihn zu verstecken.
 */
export interface PrioAnzeige {
  stufe: 1 | 2 | 3 | 4 | 5;
  /** i18n-Schlüssel der Beschriftung — übersetzt wird in der Anzeige. */
  labelKey: string;
  /** Mantine-Farbe für den Balken links an der Ticketzeile. */
  farbe: string;
  /** Steht die Priorität über der Vorgabe ihres Schemas? Dann auch als Text. */
  hervorheben: boolean;
}

/** Welches der beiden Schemata der gelieferte Wert benutzt. */
type Schema = "service" | "klassisch";

/**
 * Die Vorgabestufe je Schema. **Beide sind gemessen, nicht geraten:** `4` steht
 * als `defaultValue` in den Feldmetadaten des Projekts MS, `3` („Mittel") ist
 * die Vorgabe des klassischen Jira-Schemas.
 */
const VORGABE: Record<Schema, number> = { service: 4, klassisch: 3 };

const LABEL: Record<Schema, Record<number, string>> = {
  // SAP-Meldungspriorität — so sind die Zahlen im Serviceprojekt gemeint.
  service: {
    1: "pages.teamDaily.prio.sehrHoch",
    2: "pages.teamDaily.prio.hoch",
    3: "pages.teamDaily.prio.mittel",
    4: "pages.teamDaily.prio.niedrig",
  },
  klassisch: {
    1: "pages.teamDaily.prio.hoechste",
    2: "pages.teamDaily.prio.hoch",
    3: "pages.teamDaily.prio.mittel",
    4: "pages.teamDaily.prio.niedrig",
    5: "pages.teamDaily.prio.niedrigste",
  },
};

const FARBE: Record<number, string> = {
  1: "red.7",
  2: "orange.7",
  3: "yellow.8",
  4: "gray.5",
  5: "gray.4",
};

const NACH_NAME: Record<string, number> = {
  highest: 1,
  hoechste: 1,
  blocker: 1,
  kritisch: 1,
  critical: 1,
  high: 2,
  hoch: 2,
  major: 2,
  medium: 3,
  mittel: 3,
  normal: 3,
  low: 4,
  niedrig: 4,
  minor: 4,
  lowest: 5,
  niedrigste: 5,
  trivial: 5,
};

/** Umlaute auflösen, damit „Höchste" und „Hoechste" dieselbe Stufe treffen. */
function normalisiere(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss");
}

export function prioAnzeige(priority: string | null): PrioAnzeige {
  const roh = priority ? normalisiere(priority) : "";

  /*
   * Die FORM des Wertes entscheidet, welches Schema gilt: eine blanke Zahl
   * kommt aus dem Serviceprojekt, ein Name aus dem klassischen Schema. Eine
   * Liste bekannter Projektschlüssel wäre die schlechtere Wahl — sie wäre beim
   * nächsten neuen Projekt unvollständig, und zwar still.
   */
  const ausZahl = /^[1-5]$/.test(roh) ? Number(roh) : null;
  const schema: Schema = ausZahl === null ? "klassisch" : "service";

  const nachName = NACH_NAME[roh];
  const stufe = (ausZahl ?? nachName ?? 3) as 1 | 2 | 3 | 4 | 5;
  const gedeutet = ausZahl !== null || nachName !== undefined;

  return {
    stufe,
    labelKey: LABEL[schema][stufe] ?? "pages.teamDaily.prio.mittel",
    farbe: FARBE[stufe]!,
    hervorheben: gedeutet && stufe < VORGABE[schema],
  };
}

/**
 * Ganze Tage zwischen einem ISO-Zeitpunkt und jetzt. Für „seit 9 Tagen nicht
 * bewegt" und das Alter im Eingang. Fehlt oder faselt das Datum, kommt 0 —
 * lieber keine Auffälligkeit als eine erfundene.
 */
export function tageSeit(iso: string | null): number {
  if (!iso) return 0;
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return 0;
  return Math.max(0, Math.floor((Date.now() - ts) / 86_400_000));
}

/** Stunden aus Sekunden, deutsch formatiert („7,5 h"). */
export function stunden(sekunden: number): string {
  const h = sekunden / 3600;
  return `${h.toLocaleString("de-DE", {
    minimumFractionDigits: h < 10 ? 1 : 0,
    maximumFractionDigits: h < 10 ? 1 : 0,
  })} h`;
}
